import { useEffect, useState } from "react";
import { GroupListRow, LEVELS, Level, ListGroupsQuery } from "../../../types";
import { ApiError, GroupApi } from "../api";
import { relativeTime } from "../format";
import { useEnv } from "../providers/envProvider";
import { href, navigate } from "../route";
import { LevelBadge } from "./LevelBadge";
import { MergeDialog } from "./MergeDialog";
import { Button, Field, Panel, inputClass } from "./Shell";

// One row per group — the default view. Select two or more rows and
// "Combine" to merge them into one (nothing is deleted, see types/group.ts).
export const GroupList = ({ params }: { params: URLSearchParams }) => {
  const status = (params.get("status") || "open") as ListGroupsQuery["status"];
  const source = params.get("source") || undefined;
  const level = params.get("level") || undefined;
  const includeMerged = params.get("includeMerged") === "1";
  const q = params.get("q") || "";
  const { env } = useEnv();
  const key = `${env}|${params.toString()}`;

  const [rows, setRows] = useState<GroupListRow[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [merging, setMerging] = useState(false);

  const load = async (reset: boolean) => {
    setLoading(true);
    setError(undefined);
    try {
      const page = await GroupApi.list({
        env,
        status,
        source,
        level: level ? (level.split(",") as Level[]) : undefined,
        includeMerged,
        limit: 50,
        cursor: reset ? undefined : cursor,
      });
      setRows((prev) => (reset ? page.items : [...prev, ...page.items]));
      setCursor(page.cursor);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setSelected(new Set());
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const update = (patch: Record<string, string | undefined>) => {
    const next: Record<string, string | undefined> = { status, source, level, includeMerged: includeMerged ? "1" : undefined, q: q || undefined, ...patch };
    navigate(href.groups(next));
  };
  const toggle = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const levels = new Set((level ?? "").split(",").filter(Boolean));
  const visible = rows.filter((g) => !q || `${g.title} ${g.source} ${g.note ?? ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3">
      <Panel className="p-3 flex flex-wrap gap-3 items-end">
        <Field label="Status">
          <select className={inputClass} value={status} onChange={(e) => update({ status: e.target.value })}>
            <option value="open">open</option>
            <option value="resolved">resolved</option>
            <option value="ignored">ignored</option>
            <option value="all">all</option>
          </select>
        </Field>
        <div className="flex flex-col gap-0.5 text-xs text-neutral-500">
          Level
          <div className="flex gap-1">
            {LEVELS.map((l) => (
              <button
                key={l}
                onClick={() => {
                  const next = new Set(levels);
                  next.has(l) ? next.delete(l) : next.add(l);
                  update({ level: [...next].join(",") || undefined });
                }}
                className={`text-xs rounded px-2 py-1 border ${levels.has(l) ? "bg-neutral-900 text-white border-neutral-900 dark:bg-neutral-100 dark:text-neutral-900" : "border-neutral-300 dark:border-neutral-700"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <Field label="Source">
          <input className={inputClass} value={source ?? ""} onChange={(e) => update({ source: e.target.value || undefined })} />
        </Field>
        <Field label="Search loaded">
          <input className={inputClass} value={q} onChange={(e) => update({ q: e.target.value || undefined })} />
        </Field>
        <label className="text-xs text-neutral-500 flex items-center gap-1 pb-1.5">
          <input type="checkbox" checked={includeMerged} onChange={(e) => update({ includeMerged: e.target.checked ? "1" : undefined })} />
          show merged-away groups
        </label>
        <span className="flex-1" />
        <Button kind="primary" disabled={selected.size < 2} onClick={() => setMerging(true)}>
          Combine {selected.size > 1 ? `(${selected.size})` : ""}
        </Button>
      </Panel>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Panel>
        {visible.map((g) => (
          <div key={g.id} className={`flex items-start gap-3 px-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0 ${g.mergedInto ? "opacity-60" : ""}`}>
            <input type="checkbox" className="mt-1.5" checked={selected.has(g.id)} onChange={() => toggle(g.id)} disabled={!!g.mergedInto} />
            <a href={href.group(g.id)} className="flex-1 min-w-0 hover:underline">
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <LevelBadge level={g.level} />
                <span className="font-mono">{g.source}</span>
                {g.status !== "open" && <span className="italic">{g.status}</span>}
                {g.mergedInto && <span className="italic">merged into {g.mergedInto.slice(0, 8)}</span>}
                {g.mergedGroupIds.length > 0 && <span>+{g.mergedGroupIds.length} merged</span>}
                {(g.droppedCount ?? 0) > 0 && (
                  <span className="rounded bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200 px-1" title="Flood gate: these hits were counted but their captures were not stored">
                    {g.droppedCount} not stored
                  </span>
                )}
                <span className="flex-1" />
                <span>first {relativeTime(g.firstSeenAt)}</span>
                <span title={g.latestSeenAt}>last {relativeTime(g.latestSeenAt)}</span>
              </div>
              <div className="text-sm truncate">{g.title}</div>
            </a>
            <div className="text-right">
              <div className="text-lg font-semibold tabular-nums">{g.totalCount}</div>
              <div className="text-[10px] text-neutral-400 uppercase">events</div>
            </div>
          </div>
        ))}
        {!loading && visible.length === 0 && <p className="p-4 text-sm text-neutral-500">No groups match.</p>}
        <div className="p-2 flex items-center gap-2 text-xs text-neutral-500">
          <span>{visible.length} shown</span>
          <span className="flex-1" />
          {cursor && <Button onClick={() => load(false)} disabled={loading}>{loading ? "Loading…" : "Load more"}</Button>}
        </div>
      </Panel>
      {merging && (
        <MergeDialog
          groups={rows.filter((g) => selected.has(g.id))}
          onClose={() => setMerging(false)}
          onDone={() => {
            setMerging(false);
            setSelected(new Set());
            load(true);
          }}
        />
      )}
    </div>
  );
};
